<template>
  <el-dialog
    :model-value="modelValue"
    title="项目导出"
    width="1120px"
    top="4vh"
    destroy-on-close
    @update:model-value="emit('update:modelValue', $event)"
    @open="handleOpen"
    @closed="handleClosed"
  >
    <div class="project-export-dialog">
      <el-tabs v-model="activeExportTab" class="export-tabs">

        <!-- ── 模板导出 Tab ── -->
        <el-tab-pane label="模板与导出" name="template">
          <div class="template-tab-body">
            <ExportTemplateCenterPanel
              v-if="project"
              embedded
              :project-code="project.code"
              :project="project"
              :admin-users="adminUsers"
              @export-started="handleTemplateExportStarted"
            />
            <el-empty v-else description="请先选择项目" />
          </div>
        </el-tab-pane>

        <!-- ── 通用SQL导出 Tab ── -->
        <el-tab-pane label="通用SQL导出" name="sql">
          <section class="export-workbench-shell">
            <div class="panel-header">
              <div>
                <h3>导出配置</h3>
                <p>{{ project?.name || '-' }}（{{ project?.code || '-' }}）· {{ normalizeVersion(project?.version) }}</p>
              </div>
              <div class="export-batch-actions">
                <el-button @click="generateTasks">生成 SQL</el-button>
                <el-button type="primary" :loading="runningAll" @click="runSelectedTasks">批量执行</el-button>
              </div>
            </div>
            <div class="config-grid">
              <div class="config-block">
                <label>导出时间</label>
                <el-date-picker v-model="dateRange" type="daterange" unlink-panels value-format="YYYY-MM-DD"
                  range-separator="~" start-placeholder="开始日期" end-placeholder="结束日期"
                  :shortcuts="datePickerShortcuts" :disabled-date="disableDate" style="width: 100%" />
              </div>
              <div class="config-block">
                <label>导出模式</label>
                <el-checkbox-group v-model="selectedModes">
                  <el-checkbox v-for="item in modeOptions" :key="item.key" :label="item.key">{{ item.label }}</el-checkbox>
                </el-checkbox-group>
              </div>
              <div class="config-block">
                <label>导出内容</label>
                <el-checkbox-group v-model="selectedContents" class="content-grid">
                  <el-checkbox v-for="item in contentOptions" :key="item.key" :label="item.key">{{ item.label }}</el-checkbox>
                </el-checkbox-group>
              </div>
              <div class="config-block">
                <label>附加选项</label>
                <div class="extra-config-grid">
                  <el-checkbox v-model="byChannel">输出渠道明细</el-checkbox>
                  <div class="stay-avg-config">
                    <span>停留时长平均口径</span>
                    <el-radio-group v-model="stayAvgBase" size="small">
                      <el-radio-button label="visitUv">除以访问 UV</el-radio-button>
                      <el-radio-button label="stayUv">除以停留时长 UV</el-radio-button>
                    </el-radio-group>
                  </div>
                </div>
              </div>
            </div>
          </section>

          <section class="export-progress-shell" v-if="tasks.length">
            <div class="panel-header">
              <div>
                <h3>执行进度</h3>
                <p>已生成 {{ tasks.length }} 条 SQL，可执行项支持单独执行和批量执行；抽订单人群 SQL 仅用于复制。</p>
              </div>
              <div class="progress-summary">
                <span>已勾选 {{ enabledTaskCount }}</span>
                <span>成功 {{ successTaskCount }}</span>
                <span>失败 {{ errorTaskCount }}</span>
              </div>
            </div>
            <el-progress :percentage="overallProgress" :stroke-width="16" />
            <div class="task-batch-toolbar">
              <div class="task-batch-toolbar-hint">批量操作</div>
              <div class="task-batch-toolbar-actions">
                <el-button plain @click="toggleAllTasks(true)">全选</el-button>
                <el-button plain @click="toggleAllTasks(false)">清空</el-button>
                <el-button type="primary" plain @click="copySelectedSql">复制已勾选 SQL</el-button>
                <el-button type="success" @click="exportSelectedTaskResults">导出已执行结果</el-button>
              </div>
            </div>
          </section>

          <section class="export-task-list" v-if="tasks.length">
            <article v-for="task in tasks" :key="task.id" class="export-task-card">
              <div class="task-card-head">
                <div class="task-card-left">
                  <el-checkbox v-model="task.enabled" />
                  <div>
                    <strong>{{ task.label }}</strong>
                    <p>{{ task.copyOnly ? '复制专用 SQL' : task.statusText }}</p>
                  </div>
                </div>
                <div class="task-card-actions">
                  <el-button text @click="copySql(task.sql)">复制 SQL</el-button>
                  <el-button text @click="resetTaskSql(task)">重置</el-button>
                  <el-button text :disabled="task.copyOnly" :loading="task.running" @click="executeTask(task)">执行</el-button>
                  <el-button text type="danger" :disabled="task.copyOnly || task.status !== 'error'" @click="retryTask(task)">重试</el-button>
                  <el-button text :disabled="task.copyOnly || !task.resultRows.length" @click="exportTaskResult(task)">导出结果</el-button>
                </div>
              </div>
              <el-input v-model="task.sql" type="textarea" :rows="task.expanded ? 16 : 8" resize="vertical" class="task-sql-editor" />
              <div class="task-card-meta">
                <el-button text @click="task.expanded = !task.expanded">{{ task.expanded ? '收起 SQL' : '展开 SQL' }}</el-button>
                <el-tag size="small" :type="task.copyOnly ? 'info' : task.statusType">{{ task.copyOnly ? '仅复制' : task.statusText }}</el-tag>
              </div>
              <div v-if="task.errorMessage" class="task-error-box">{{ task.errorMessage }}</div>
              <div v-if="!task.copyOnly && task.resultRows.length" class="task-preview-shell">
                <div class="panel-header">
                  <div><h3>结果预览</h3><p>共 {{ task.resultRows.length }} 行</p></div>
                </div>
                <el-table :data="task.resultRows" stripe max-height="260">
                  <el-table-column v-for="column in task.columns" :key="column" :prop="column" :label="column" min-width="140" show-overflow-tooltip>
                    <template #default="{ row }">{{ formatPreviewCell(row, column) }}</template>
                  </el-table-column>
                </el-table>
              </div>
            </article>
          </section>

          <section v-else class="empty-task-shell">
            <el-empty description="先选择导出内容和模式，再生成 SQL" />
          </section>
        </el-tab-pane>
      </el-tabs>
    </div>

    <template #footer>
      <el-button @click="emit('update:modelValue', false)">关闭</el-button>
    </template>
  </el-dialog>
</template>

<script setup>
import { computed, ref, watch } from 'vue'
import { ElMessage } from 'element-plus'
import api from '@/utils/http'
import { buildExportFileName, buildGroupedDateReportSheet, buildTableSheetFromRows, exportWorkbook } from '../reportDataExport'
import { buildProjectExportTasks, PROJECT_EXPORT_CONTENTS, PROJECT_EXPORT_MODES } from './projectExportSqlBuilders'
import ExportTemplateCenterPanel from '../components/ExportTemplateCenterPanel.vue'
const props = defineProps({
  modelValue:   { type: Boolean, default: false },
  project:      { type: Object,  default: null },
  defaultRange: { type: Array,   default: () => [] },
  adminUsers:   { type: Array,   default: () => [] }
})
const emit = defineEmits(['update:modelValue'])

// ── Tab 控制 ──
const activeExportTab = ref('sql')

function handleOpen() {
  // 默认进入「模板与导出」，便于查看模板与通用导出记录（SQL 导出在另一 Tab）
  activeExportTab.value = props.project?.code ? 'template' : 'sql'
}

function handleTemplateExportStarted() {
  ElMessage.success('导出任务已启动，请勿关闭当前页面，成功完成后可在导出记录中查看历史记录')
}

// ── 通用SQL导出逻辑 ──
const contentOptions = PROJECT_EXPORT_CONTENTS
const modeOptions    = PROJECT_EXPORT_MODES
const dateRange        = ref([])
const selectedContents = ref(['flow', 'click', 'stayTime'])
const selectedModes    = ref(['dailySum'])
const byChannel        = ref(false)
const stayAvgBase      = ref('visitUv')
const tasks            = ref([])
const runningAll       = ref(false)

const overallProgress  = computed(() => {
  if (!tasks.value.length) return 0
  return Math.round(tasks.value.filter(i => i.status === 'success' || i.status === 'error').length / tasks.value.length * 100)
})
const enabledTaskCount = computed(() => tasks.value.filter(i => i.enabled).length)
const successTaskCount = computed(() => tasks.value.filter(i => i.status === 'success').length)
const errorTaskCount   = computed(() => tasks.value.filter(i => i.status === 'error').length)

const datePickerShortcuts = [
  { text: '昨天',   value: () => { const d = rd(-1); return [new Date(d), new Date(d)] } },
  { text: '近3天',  value: () => [new Date(rd(-3)), new Date(rd(-1))] },
  { text: '近7天',  value: () => [new Date(rd(-7)), new Date(rd(-1))] },
  { text: '近1个月',value: () => [new Date(rd(-30)),new Date(rd(-1))] },
  { text: '近3个月',value: () => [new Date(rd(-90)),new Date(rd(-1))] }
]
function rd(n) { const d = new Date(); d.setDate(d.getDate()+n); return fmt(d) }
function fmt(d) { return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}` }

watch(() => props.modelValue, v => { if (v) initializeState() }, { immediate: true })
watch(dateRange, () => { if (tasks.value.length) regenerateTasksForCurrentConfig() }, { deep: true })
watch([byChannel, stayAvgBase, selectedModes, selectedContents], () => { if (tasks.value.length) regenerateTasksForCurrentConfig() }, { deep: true })

function initializeState() {
  dateRange.value = Array.isArray(props.defaultRange) && props.defaultRange.length ? [...props.defaultRange] : [rd(-7), rd(-1)]
  selectedContents.value = ['flow', 'click', 'stayTime']
  selectedModes.value    = ['dailySum']
  byChannel.value        = false
  stayAvgBase.value      = 'visitUv'
  tasks.value            = []
}
function handleClosed() { initializeState() }
function disableDate(date) { const t = new Date(); t.setHours(0,0,0,0); return date.getTime() >= t.getTime() }
function normalizeVersion(v) { return String(v||'v4').toLowerCase() === 'v3' ? 'V3' : 'V4' }
function generateTasks() { const g = buildTasksFromCurrentConfig(); if (g) tasks.value = g }

function buildTasksFromCurrentConfig() {
  if (!props.project?.code)          { ElMessage.warning('缺少项目信息'); return null }
  if (!dateRange.value?.[0])         { ElMessage.warning('请选择导出日期'); return null }
  if (!selectedContents.value.length){ ElMessage.warning('请至少勾选一个导出内容'); return null }
  if (!selectedModes.value.length && !selectedContents.value.includes('orderCrowd')) { ElMessage.warning('请至少勾选一个导出模式'); return null }
  return buildProjectExportTasks({
    project: props.project, startDate: dateRange.value[0], endDate: dateRange.value[1],
    selectedContents: selectedContents.value, selectedModes: selectedModes.value,
    byChannel: byChannel.value, stayAvgBase: stayAvgBase.value
  }).map(item => ({
    ...item, originalSql: item.sql, enabled: true, expanded: false, running: false,
    status: 'idle', statusText: '待执行', statusType: 'info', errorMessage: '', resultRows: [], columns: []
  }))
}

function regenerateTasksForCurrentConfig() {
  const prev = new Map(tasks.value.map(i => [`${i.contentKey}_${i.mode}`, i]))
  const g = buildTasksFromCurrentConfig()
  if (!g) return
  tasks.value = g.map(t => { const p = prev.get(`${t.contentKey}_${t.mode}`); if (p) { t.enabled = p.enabled; t.expanded = p.expanded } return t })
  ElMessage.success('SQL 已自动重新生成')
}

async function runSelectedTasks() {
  const list = tasks.value.filter(i => i.enabled && !i.copyOnly)
  if (!list.length) { ElMessage.warning('请至少勾选一条可执行 SQL'); return }
  runningAll.value = true
  try { for (const t of list) await executeTask(t) } finally { runningAll.value = false }
}
function toggleAllTasks(v) { tasks.value.forEach(i => i.enabled = v) }

async function executeTask(task) {
  if (task.copyOnly) { ElMessage.info('这条 SQL 仅用于复制'); return }
  Object.assign(task, { running: true, status: 'running', statusText: '执行中', statusType: 'warning', errorMessage: '' })
  try {
    const result = await api.executeDataWorksSql(task.sql)
    if (result?.success === false) throw new Error(result?.errorMsg || 'SQL 执行失败')
    const rows = Array.isArray(result?.data) ? result.data : []
    Object.assign(task, { resultRows: rows, columns: rows.length ? Object.keys(rows[0]) : [],
      status: 'success', statusText: `执行成功${rows.length ? `，${rows.length} 行` : '，无结果'}`, statusType: 'success' })
  } catch (e) {
    Object.assign(task, { status: 'error', statusText: '执行失败', statusType: 'danger', errorMessage: e.message || 'SQL 执行失败' })
  } finally { task.running = false }
}
async function retryTask(task) { await executeTask(task) }
function resetTaskSql(task) { task.sql = task.originalSql }
async function copySql(sql) {
  try { await navigator.clipboard.writeText(sql); ElMessage.success('SQL 已复制') } catch { ElMessage.error('复制失败') }
}
async function copySelectedSql() {
  const list = tasks.value.filter(i => i.enabled)
  if (!list.length) { ElMessage.warning('请先勾选 SQL'); return }
  await copySql(list.map(i => `-- ${i.label}\n${i.sql}`).join('\n\n'))
}
function exportTaskResult(task) {
  if (!task.resultRows.length) { ElMessage.warning('请先执行 SQL'); return }
  const sheet = buildSingleTaskReportSheet(task)
  exportWorkbook({ sheets: [sheet], fileName: buildExportFileName({ projectName: props.project?.name, projectCode: props.project?.code, dataLabel: task.label, startDate: dateRange.value?.[0]||'start', endDate: dateRange.value?.[1]||'end' }) })
}
function exportSelectedTaskResults() {
  const ready = tasks.value.filter(i => i.enabled && !i.copyOnly && i.resultRows.length)
  if (!ready.length) { ElMessage.warning('请先执行并勾选至少一条结果'); return }
  const sheets = []
  const metric = ready.filter(i => i.contentKey !== 'orderCrowd')
  const crowd  = ready.filter(i => i.contentKey === 'orderCrowd')
  if (metric.length) sheets.push(buildCombinedReportSheet(metric))
  crowd.forEach(i => sheets.push(buildTableSheetFromRows({ sheetName: i.label, rows: i.resultRows, themeKey: 'ocean' })))
  exportWorkbook({ sheets, fileName: buildExportFileName({ projectName: props.project?.name, projectCode: props.project?.code, dataLabel: '项目导出结果', startDate: dateRange.value?.[0]||'start', endDate: dateRange.value?.[1]||'end' }) })
}
function buildSingleTaskReportSheet(task) {
  if (task.copyOnly || task.contentKey === 'orderCrowd') return buildTableSheetFromRows({ sheetName: task.label, rows: task.resultRows, themeKey: 'ocean' })
  const report = buildTaskReportColumns([task])
  return buildGroupedDateReportSheet({ sheetName: task.label, dateList: report.dateList, groups: report.groups, includeDailySumRow: task.includeDailySum, includeDedupRow: task.includeDedup, themeKey: 'ocean' })
}
function buildCombinedReportSheet(taskList) {
  const report = buildTaskReportColumns(taskList)
  return buildGroupedDateReportSheet({ sheetName: '项目导出报表', dateList: report.dateList, groups: report.groups, includeDailySumRow: taskList.some(i => i.includeDailySum), includeDedupRow: taskList.some(i => i.includeDedup), themeKey: 'ocean' })
}
function buildTaskReportColumns(taskList) {
  const dateList = buildDateList(dateRange.value?.[0], dateRange.value?.[1])
  const groups = []
  taskList.forEach(task => {
    const metrics = resolveTaskMetrics(task.resultRows).map(key => ({ key, label: resolveMetricLabel(key), format: resolveMetricFormat(key) }))
    const dailyRows = task.resultRows.filter(r => r.time_scope && r.time_scope !== '__dedup__')
    const dedupRows = task.resultRows.filter(r => r.time_scope === '__dedup__')
    Array.from(new Set(dailyRows.map(r => resolveTaskGroupLabel(r)))).sort((a,b) => a.localeCompare(b,'zh-CN')).forEach(sub => {
      const label = sub === task.label ? task.label : `${task.label}-${sub}`
      const valuesByDate = {}
      dateList.forEach(d => { valuesByDate[d] = createMetricValueShell(metrics.map(m => m.key)) })
      dailyRows.filter(r => resolveTaskGroupLabel(r) === sub).forEach(r => { metrics.forEach(m => { valuesByDate[r.time_scope][m.key] = toNumber(r[m.key]) }) })
      const dailySum = createMetricValueShell(metrics.map(m => m.key))
      dateList.forEach(d => metrics.forEach(m => { dailySum[m.key] += toNumber(valuesByDate[d][m.key]) }))
      const dedupRow = dedupRows.find(r => resolveTaskGroupLabel(r) === sub) || {}
      const dedup = createMetricValueShell(metrics.map(m => m.key))
      metrics.forEach(m => { dedup[m.key] = toNumber(dedupRow[m.key]) })
      groups.push({ label, metrics, valuesByDate, summary: { dailySum, dedup } })
    })
  })
  return { dateList, groups }
}
function resolveTaskMetrics(rows) { const skip = new Set(['title','time_scope','channel','index_name','code']); return Object.keys(rows[0]||{}).filter(k => !skip.has(k)) }
function resolveTaskGroupLabel(r) { if (r.channel && r.index_name) return `${r.channel}-${r.index_name}`; if (r.channel) return r.channel; if (r.index_name) return r.index_name; return r.title || '数据' }
function resolveMetricLabel(k) { return { pv:'PV', uv:'UV', timeOnSite:'停留时长', totalTimeOnSite:'总停留时长', avg_stay_seconds:'平均停留(秒)', clickUser:'点击人数', clickCount:'点击次数', sumStayTime:'总停留时长', avgStayTime:'平均停留时长', clickRate:'点击率', dropRate:'流失率', pvRate:'PV占比', uvRate:'UV占比', sevenDayReturnUser:'7日回访' }[k] || k }
function resolveMetricFormat(k) { if (['clickRate','dropRate','pvRate','uvRate'].includes(k)) return 'percent'; if (['avgStayTime','avg_stay_seconds'].includes(k)) return 'decimal'; return 'integer' }
function createMetricValueShell(metrics) { return metrics.reduce((a,m) => { a[m]=0; return a }, {}) }
function formatPreviewCell(row, col) {
  const v = row?.[col]
  if (v === null || v === undefined || v === '') return ''
  if (typeof v === 'number' && isFinite(v) || (typeof v === 'string' && v.trim() && isFinite(Number(v)))) {
    const n = Number(v), fmt = resolveMetricFormat(col)
    if (fmt === 'percent') return `${(n*100).toFixed(2)}%`
    if (fmt === 'decimal') return new Intl.NumberFormat('zh-CN',{maximumFractionDigits:2}).format(n)
    return new Intl.NumberFormat('zh-CN',{maximumFractionDigits:0}).format(n)
  }
  return v
}
function toNumber(v) { if (v===null||v===undefined||v==='') return 0; const n=Number(v); return isFinite(n)?n:0 }
function buildDateList(s, e) { const list=[],c=new Date(s),end=new Date(e); while(c<=end){list.push(fmt(c));c.setDate(c.getDate()+1)} return list }
</script>

<style scoped>
.project-export-dialog { display: flex; flex-direction: column; gap: 16px; max-height: 78vh; overflow: auto; padding-right: 4px; }
.template-tab-body {
  height: 62vh;
  min-height: 420px;
}
.export-tabs :deep(.el-tabs__content) {
  overflow: visible;
}
.export-workbench-shell, .export-progress-shell, .export-task-card, .empty-task-shell { border: 1px solid #e2e8f0; border-radius: 8px; background: #fff; padding: 16px; }
.config-grid { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 16px; }
.config-block { display: flex; flex-direction: column; gap: 8px; }
.config-block label { color: #475569; font-size: 13px; font-weight: 600; }
.content-grid, .extra-config-grid { display: grid; gap: 10px; }
.content-grid { grid-template-columns: repeat(2, minmax(0, 1fr)); }
.stay-avg-config { display: flex; flex-direction: column; gap: 8px; }
.stay-avg-config span { color: #475569; font-size: 12px; font-weight: 600; }
.export-batch-actions, .task-card-actions, .task-card-meta, .task-card-left, .task-batch-toolbar, .progress-summary { display: flex; }
.export-batch-actions, .task-card-actions { gap: 8px; }
.task-batch-toolbar, .progress-summary { gap: 12px; flex-wrap: wrap; }
.task-batch-toolbar { margin-top: 12px; padding: 12px 14px; border-radius: 8px; border: 1px solid #dbeafe; background: #f8fbff; align-items: center; justify-content: space-between; }
.progress-summary { color: #64748b; font-size: 12px; }
.task-batch-toolbar-hint { color: #1d4ed8; font-size: 13px; font-weight: 600; white-space: nowrap; }
.task-batch-toolbar-actions { display: flex; gap: 10px; flex-wrap: wrap; }
.task-batch-toolbar-actions :deep(.el-button) { min-width: 120px; }
.task-card-left { gap: 10px; align-items: flex-start; }
.task-card-left strong { color: #0f172a; }
.task-card-left p { margin: 4px 0 0; color: #64748b; font-size: 12px; }
.export-task-list { display: flex; flex-direction: column; gap: 14px; }
.task-card-head { display: flex; justify-content: space-between; gap: 16px; align-items: flex-start; margin-bottom: 12px; }
.task-sql-editor { margin-bottom: 10px; }
.task-card-meta { justify-content: space-between; align-items: center; margin-bottom: 10px; }
.task-error-box { margin-bottom: 10px; border-radius: 8px; background: #fff1f2; border: 1px solid #fecdd3; color: #be123c; padding: 10px 12px; font-size: 12px; line-height: 1.6; }
.task-preview-shell { margin-top: 12px; }
.panel-header { display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 12px; }
.panel-header h3 { margin: 0; font-size: 16px; color: #0f172a; }
.panel-header p { margin: 4px 0 0; font-size: 12px; color: #64748b; }
@media (max-width: 960px) {
  .config-grid { grid-template-columns: minmax(0, 1fr); }
  .task-card-head { flex-direction: column; }
  .task-card-actions, .task-batch-toolbar-actions { flex-wrap: wrap; }
  .task-batch-toolbar { align-items: stretch; }
}
</style>
